import React, { useRef, useState, useEffect } from "react";

// prime react component
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dialog } from "primereact/dialog";

// formik component
import { Formik, Form, ErrorMessage } from "formik";

// datatable component
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
  statusDropdownItems,
} from "../../../appconfig/Settings";

// equipment table column field
import { EquipmentColumns } from "../../../appconfig/DataTableColumns";

//  api services
import {
  getAllDetails,
  postAllData,
  updateData,
} from "../../../service/ProductService";

// api slug from apiconstant
import {
  ADD_EQUIPMENT,
  CHANGE_EQUIPMENT_STATUS,
  GET_EQUIPMENT_LIST,
  UPDATE_EQUIPMENT,
} from "../../../config/ApiConstant";

// equipment validation schema
import { EquipmentValidationSchema } from "../../../config/Validate";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";

// type define for field
import { equipmentType } from "../../../appconfig/interfaces";
import { OperatorTab } from "../OperatorsTab";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";

const Equipments: React.FC = () => {
  document.title = "Equipments | Agriown";

  //  ref hook
  const formikRef = useRef<any>(null);

  // Set Toast
  const toast = useRef<Toast>(null);

  // state for Filter
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  // state for dialog
  const [addEquipmentDialog, setEquipmentDialog] = useState<boolean>(false);

  // state for loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // state for store equipments list
  const [equipmentList, setEquipmentList] = useState<equipmentType[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedEquipment, setSelectedEquipment] = useState<any>([]);

  const [confirmStatusDialog, setConfirmStatusDialog] =
    useState<boolean>(false);
  const [statusChangeData, setStatusChangeData] = useState<{
    id: number;
    newStatus: boolean;
  } | null>(null);
  const [statusFilterValue, setStatusFilterValue] = useState<any>(null);

  // state for slot selection
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    equipment_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // initial values define for equipment field validation based on edit mode
  const initialFormValues: equipmentType = {
    equipment_name:
      editMode && selectedEquipment ? selectedEquipment.equipment_name : "",
    equipment_name_hi:
      editMode && selectedEquipment
        ? selectedEquipment.equipment_name_hi || ""
        : "",
    equipment_name_gu:
      editMode && selectedEquipment
        ? selectedEquipment.equipment_name_gu || ""
        : "",
  };

  // Fetch equipment list on page load
  useEffect(() => {
    getEquipmentList();
  }, []);

  // equipment list api call
  const getEquipmentList = async () => {
    try {
      const response = await getAllDetails(GET_EQUIPMENT_LIST);

      if (response) {
        setEquipmentList(response);
        setPageLoad(true);
      } else {
        setEquipmentList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  //  add & edit equipment function api call
  const handleEquipmentSubmit = async (
    values: equipmentType,
    { resetForm }: any
  ) => {
    setLoading(true);
    try {
      const payload: any = {
        equipment_name: values.equipment_name,
      };

      if (values.equipment_name_hi && values.equipment_name_hi.trim() !== "") {
        payload.equipment_name_hi = values.equipment_name_hi;
      }
      if (values.equipment_name_gu && values.equipment_name_gu.trim() !== "") {
        payload.equipment_name_gu = values.equipment_name_gu;
      }

      const response =
        editMode && selectedEquipment.id !== null
          ? await updateData(UPDATE_EQUIPMENT, selectedEquipment.id, payload)
          : await postAllData(ADD_EQUIPMENT, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setEquipmentDialog(false);
        setEditMode(false);
        getEquipmentList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // change equipment status api call
  const changeEquipmentStatus = async (
    equipmentId: number,
    newStatus: boolean
  ) => {
    try {
      const payload = {
        status: newStatus,
      };
      const response = await updateData(
        CHANGE_EQUIPMENT_STATUS,
        equipmentId,
        payload
      );

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        getEquipmentList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // Edit button handler
  const edit = (rowData: equipmentType) => {
    setEditMode(true);
    setSelectedEquipment(rowData);
    setEquipmentDialog(true);
  };

  // Action column with edit icon
  const actionBodyTemplate = (rowData: equipmentType) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        className="mr-2 p-button-secondary"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() => edit(rowData)}
      />
    </div>
  );

  // Toolbar - Left
  const leftToolbarTemplate = () => (
    <>
      <span className="mr-0 md:mr-3 search-bar">
        <i className="pi pi-search" />
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    </>
  );

  // Clear Filters
  const clear = () => {
    setGlobalFilter("");
    setFilters({ ...emptyFilters });
    setStatusFilterValue(null);
  };

  // Toolbar - Right
  const rightToolbarTemplate = () => (
    <>
      <Button
        icon="pi pi-filter-slash"
        label="Clear"
        onClick={() => clear()}
        className="md:mr-2 p-button-outlined p-button-primary"
      />
      <Button
        label="Add Equipment"
        icon="pi pi-plus"
        className="ml-2 p-button toolbar-button"
        onClick={() => {
          setEditMode(false);
          setEquipmentDialog(true);
        }}
      />
    </>
  );

  // Handle confirmed status change
  const handleConfirmStatusChange = () => {
    if (statusChangeData) {
      changeEquipmentStatus(statusChangeData.id, statusChangeData.newStatus);
      hideStatusDialog();
    }
  };

  // open confirm status change dialog
  const confirmChangeStatus = (id: number, newStatus: boolean) => {
    setStatusChangeData({ id, newStatus });
    setConfirmStatusDialog(true);
  };

  // Hide confirm dialog
  const hideStatusDialog = () => {
    setConfirmStatusDialog(false);
    setStatusChangeData(null);
  };

  //status Template
  const statusTemplate = (rowData: any) => {
    let statusClass =
      rowData.status === true
        ? "status status-active"
        : "status status-inactive";

    return (
      <div className="flex flex-column align-items-center">
        <InputSwitch
          checked={rowData.status}
          className="mb-2 mr-2"
          onChange={(e) => {
            confirmChangeStatus(rowData.id, e.value);
          }}
        />
        <span className={statusClass}>
          {rowData.status === true ? "Active" : "In Active"}
        </span>
      </div>
    );
  };

  //search by type Filter Template
  const searchByType = (options: any) => {
    return (
      <Dropdown
        filter
        value={statusFilterValue}
        options={statusDropdownItems}
        onChange={(e) => {
          setStatusFilterValue(e.value);
          options.filterCallback(e.value ? e.value.code : null, options.index);
        }}
        optionLabel="name"
        placeholder="Search By Status"
        className="order-drp"
        showClear
      />
    );
  };

  // Hide dialog and reset state
  const hideAddEquipmentDialog = () => {
    setEquipmentDialog(false);
    setEditMode(false);
  };

  // model footer button cancel & submit
  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={hideAddEquipmentDialog}
        className="p-button-secondary"
      />
      <Button
        label={editMode ? "Update" : "Save"}
        icon="pi pi-check"
        type="button"
        loading={loading}
        disabled={loading}
        onClick={() => formikRef.current?.handleSubmit()}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <OperatorTab index={1} />
            <Toast ref={toast} />

            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

            {pageLoad ? (
              <DataTable
                value={equipmentList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                rows={defaultRowOptions}
                paginator={equipmentList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                emptyMessage="No Data Found"
                removableSort
              >
                {EquipmentColumns.map((col) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  } else if (col.field === "equipment_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        sortable
                        filter
                        filterElement={globalNameFilterTemplate}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "status") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={statusTemplate}
                        filter
                        filterElement={searchByType}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        sortable
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {EquipmentColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* Equiipment status confirm */}
      <Dialog
        visible={confirmStatusDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "450px" }}
        draggable={false}
        header="Change Equipment Status"
        modal
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={hideStatusDialog}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleConfirmStatusChange}
              loading={loading}
            />
          </>
        }
        onHide={hideStatusDialog}
      >
        <div className="flex align-items-center justify-content-start">
          <i
            className="mr-3 pi pi-exclamation-triangle delete-triangle"
            style={{ fontSize: "2rem" }}
          />
          <span>
            Are you sure you want to set this Equipment Status as{" "}
            <b>{statusChangeData?.newStatus ? "Active" : "Inactive"}</b>?
          </span>
        </div>
      </Dialog>

      {/* Add/Edit Equipment Dialog with Formik */}
      <Dialog
        visible={addEquipmentDialog}
        style={{ width: "450px" }}
        header={editMode ? "Edit Equipment" : "Add Equipment"}
        modal
        className="mx-3 lg:mx-0"
        draggable={false}
        onHide={hideAddEquipmentDialog}
        footer={footerContent}
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={EquipmentValidationSchema}
          enableReinitialize
          innerRef={formikRef}
          onSubmit={handleEquipmentSubmit}
        >
          {({
            handleSubmit,
            values,
            handleBlur,
            handleChange,
            touched,
            errors,
          }) => (
            <Form className="grid p-fluid" onSubmit={handleSubmit}>
              <div className="field col-12">
                <label htmlFor="equipment_name">Name (English) *</label>
                <InputText
                  id="equipment_name"
                  name="equipment_name"
                  value={values.equipment_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.equipment_name && errors.equipment_name
                      ? "p-invalid"
                      : ""
                  }
                />

                <small className="p-error">
                  <ErrorMessage name="equipment_name" />
                </small>
              </div>

              {/* Hindi Equipment Name */}
              <div className="field col-12">
                <label htmlFor="equipment_name_hi">Name (Hindi)</label>
                <InputText
                  id="equipment_name_hi"
                  name="equipment_name_hi"
                  value={values.equipment_name_hi}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              {/* Gujarati equipment Name */}
              <div className="field col-12">
                <label htmlFor="equipment_name_gu">Name (Gujarati)</label>
                <InputText
                  id="equipment_name_gu"
                  name="equipment_name_gu"
                  value={values.equipment_name_gu}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default Equipments;
